
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Spinner, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';

const AboutBlockForm = ({ show, onHide, onSubmit, initialData }) => {
    const [formData, setFormData] = useState({});
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const isNew = !initialData;

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                content: {
                    // Imposta i valori di default come fallback
                    section_title: '',
                    main_text: '',
                    background_color: '#ffffff',
                    font_family: 'Default',
                    font_size_preset: 'Default',
                    object_fit: 'cover', // Nuovo default
                    ...initialData.content // Sovrascrivi i default con i valori esistenti
                }
            });
            if (initialData.content?.image_url) {
                const staticBaseUrl = process.env.REACT_APP_STATIC_FILES_BASE_URL || 'http://localhost:8000';
                let imageUrl = initialData.content.image_url;
                if (imageUrl.startsWith('/uploads/') && !imageUrl.startsWith('/api/uploads/')) {
                    imageUrl = '/api' + imageUrl;
                }
                setPreviewUrl(staticBaseUrl + imageUrl);
            } else {
                setPreviewUrl(null);
            }
        } else {
            setFormData({
                title: '',
                block_type: 'text_block', // Default a text_block
                is_active: true,
                order: 0,
                content: {
                    // Defaults completi per un nuovo blocco
                    section_title: '',
                    main_text: '',
                    background_color: '#ffffff',
                    font_family: 'Default',
                    font_size_preset: 'Default',
                    object_fit: 'cover', // Nuovo default per i nuovi blocchi
                }
            });
            setPreviewUrl(null);
        }
        setImageFile(null);
    }, [initialData, show]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContentChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [name]: value
            }
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let updatedFormData = { ...formData };
            // Se c'è un file immagine da caricare
            if (imageFile && formData.block_type === 'image_block') {
                const uploadData = new FormData();
                uploadData.append('file', imageFile);
                // Usa il titolo blocco come nome file
                uploadData.append('block_title', formData.title || 'immagine-chisiamo');
                const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                const res = await axios.post(`${apiBase}/api/upload/image`, uploadData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                });
                if (res.data && res.data.image_url) {
                    updatedFormData.content = {
                        ...updatedFormData.content,
                        image_url: res.data.image_url,
                    };
                    // Aggiorna preview con url finale
                    setPreviewUrl(`${apiBase}${res.data.image_url.startsWith('/') ? res.data.image_url : '/' + res.data.image_url}`);
                } else {
                    throw new Error('Upload immagine fallito');
                }
            }
            await onSubmit(updatedFormData);
            toast.success(isNew ? "Blocco creato con successo!" : "Blocco aggiornato con successo!");
            onHide();
        } catch (err) {
            console.error("Errore nel salvataggio del blocco:", err);
            toast.error("Errore nel salvataggio del blocco.");
        } finally {
            setLoading(false);
        }
    };

    const renderContentFields = () => {
        // Campi comuni di stile/layout
        const styleFields = <>
            <hr/>
            <h6>Stile e Layout</h6>
            <Form.Group className="mb-3">
                <Form.Label>Colore Sfondo</Form.Label>
                <Form.Control
                    type="color"
                    name="background_color"
                    value={formData.content?.background_color || '#ffffff'}
                    onChange={handleContentChange}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Colore Testo</Form.Label>
                <Form.Control
                    type="color"
                    name="text_color"
                    value={formData.content?.text_color || '#000000'}
                    onChange={handleContentChange}
                />
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Famiglia Font</Form.Label>
                <Form.Select
                    name="font_family"
                    value={formData.content?.font_family || 'Default'}
                    onChange={handleContentChange}
                >
                    <option value="Default">Default (sito)</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label>Dimensione Font</Form.Label>
                <Form.Select
                    name="font_size_preset"
                    value={formData.content?.font_size_preset || 'Default'}
                    onChange={handleContentChange}
                >
                    <option value="Default">Default</option>
                    <option value="text-sm">Small</option>
                    <option value="text-base">Base</option>
                    <option value="text-lg">Large</option>
                    <option value="text-xl">Extra Large</option>
                    <option value="text-2xl">2XL</option>
                    <option value="text-3xl">3XL</option>
                    <option value="text-4xl">4XL</option>
                </Form.Select>
            </Form.Group>
        </>;
        switch (formData.block_type) {
            case 'text_block':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo Sezione</Form.Label>
                            <Form.Control type="text" name="section_title" value={formData.content?.section_title || ''} onChange={handleContentChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Testo Principale</Form.Label>
                            <Form.Control as="textarea" rows={4} name="main_text" value={formData.content?.main_text || ''} onChange={handleContentChange} />
                        </Form.Group>
                        {styleFields}
                    </>
                );
            case 'image_block':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Immagine</Form.Label>
                            <Form.Control type="file" name="image_file" onChange={handleFileChange} accept="image/*" />
                            {formData.content?.image_url && (
                                <Form.Group className="mt-2">
                                    <Form.Label>Percorso Immagine Salvato (Debug)</Form.Label>
                                    <Form.Control type="text" readOnly value={formData.content.image_url} />
                                </Form.Group>
                            )}
                            {previewUrl && <Image src={previewUrl} thumbnail fluid className="mt-3" style={{ maxHeight: '200px' }} />}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Adattamento Immagine</Form.Label>
                            <Form.Select
                                name="object_fit"
                                value={formData.content?.object_fit || 'cover'}
                                onChange={handleContentChange}
                            >
                                <option value="cover">Copri Contenitore</option>
                                <option value="contain">Contieni nel Contenitore</option>
                            </Form.Select>
                        </Form.Group>
                        {styleFields}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{isNew ? 'Nuovo Blocco Chi Siamo' : 'Modifica Blocco Chi Siamo'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Titolo Blocco (interno)</Form.Label>
                        <Form.Control type="text" name="title" value={formData.title || ''} onChange={handleChange} required />
                        <Form.Text>Questo titolo è solo per riferimento interno.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo Blocco</Form.Label>
                        <Form.Select name="block_type" value={formData.block_type || ''} onChange={handleChange} required disabled={!isNew ? true : false}>
                            <option value="text_block">Testo</option>
                            <option value="image_block">Immagine</option>
                        </Form.Select>
                        {!isNew && <Form.Text>Il tipo di blocco non può essere modificato.</Form.Text>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check type="switch" id="is_active-switch" label="Blocco Attivo" name="is_active" checked={formData.is_active || false} onChange={handleChange} />
                    </Form.Group>
                    <hr />
                    <h5>Contenuto del Blocco</h5>
                    {renderContentFields()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Annulla
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Salva'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AboutBlockForm;
